import { FC, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import { FileText } from 'lucide-react'
import { cn } from '../lib/utils'

interface TermsOfServiceDialogProps {
  className?: string
  triggerClassName?: string
  linkText?: string
  variant?: 'link' | 'button'
}

const TermsOfServiceDialog: FC<TermsOfServiceDialogProps> = ({
  className,
  triggerClassName,
  linkText = 'Terms of Service',
  variant = 'link',
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 cursor-pointer transition-colors',
            variant === 'link' &&
              'text-sumoPrimary hover:text-sumoOrange underline underline-offset-4',
            variant === 'button' &&
              'text-neutral700 dark:text-neutral300 hover:text-sumoPrimary dark:hover:text-sumoPrimary',
            triggerClassName
          )}
        >
          <FileText className="w-4 h-4" />
          <span className="text-sm font-medium">{linkText}</span>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-sumoPrimary" />
            Terms of Service
          </DialogTitle>
          <DialogDescription>
            Please read these terms carefully before using InventorySumo
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2 space-y-6 text-sm text-neutral700 dark:text-neutral300">
          {/* Last Updated */}
          <div className="text-xs text-neutral500 dark:text-neutral500">
            <strong>Last Updated:</strong> March 2026
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              1. Agreement to Terms
            </h2>
            <p>
              By accessing or using InventorySumo ("Service", "Application", "Software"), you agree
              to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms,
              do not use the Service.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              2. Description of Service
            </h2>
            <p>
              InventorySumo is a web-based inventory management and listing platform that enables
              users to track physical inventory, manage storage units, create product listings, and
              record sales transactions.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              3. Intellectual Property Rights
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              3.1 Ownership
            </h3>
            <p className="mb-3">
              All rights, title, and interest in and to the Service, including all software, code,
              designs, graphics, text, user interfaces, visual interfaces, photographs, trademarks,
              logos, sounds, music, artwork, and computer code (collectively, "Content"), are and
              will remain the exclusive property of InventorySumo and its licensors.
            </p>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              3.2 License Grant
            </h3>
            <p className="mb-3">
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
              non-transferable, non-sublicensable, revocable license to access and use the Service
              for your internal business purposes only.
            </p>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              3.3 Restrictions
            </h3>
            <p className="mb-2">You may not:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Copy, modify, distribute, sell, or lease any part of the Service</li>
              <li>
                Reverse engineer, decompile, or attempt to extract the source code of the Service
              </li>
              <li>
                Remove, alter, or obscure any proprietary notices (including copyright and trademark
                notices)
              </li>
              <li>Use the Service to build a competitive product or service</li>
              <li>Frame or mirror any part of the Service without our express written permission</li>
              <li>Use any automated system (bots, scrapers, etc.) to access the Service</li>
            </ul>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              3.4 User Data
            </h3>
            <p>
              You retain all ownership rights to the data you input into the Service ("User Data").
              By using the Service, you grant us a limited license to host, store, and process your
              User Data solely for the purpose of providing the Service to you.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              4. User Responsibilities and Assumption of Risk
            </h2>

            <div className="p-4 bg-warning500/10 border-2 border-warning500 rounded-lg mb-3">
              <p className="font-semibold text-warning700 dark:text-warning300">
                YOU ASSUME ALL RESPONSIBILITY AND RISK FOR YOUR USE OF THE SERVICE.
              </p>
            </div>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              4.1 Your Responsibility
            </h3>
            <p className="mb-2">You acknowledge that:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
              <li>You are solely responsible for the accuracy, quality, and legality of your User Data</li>
              <li>
                You are solely responsible for all business decisions made based on information
                provided by the Service
              </li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You are responsible for maintaining backup copies of your User Data</li>
            </ul>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              4.2 Inventory and Financial Data
            </h3>
            <p className="mb-2">You acknowledge that:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                The Service is a tool to assist with inventory management and does not replace
                professional accounting, legal, or business advice
              </li>
              <li>
                You are solely responsible for verifying the accuracy of all inventory counts,
                valuations, and financial calculations
              </li>
              <li>
                We are not responsible for any business losses, inventory discrepancies, or
                financial errors that may occur from your use of the Service
              </li>
              <li>
                You should independently verify all critical data and maintain separate records
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              5. Disclaimer of Warranties
            </h2>
            <div className="p-4 bg-error500/10 border-2 border-error500 rounded-lg">
              <p className="font-semibold text-error700 dark:text-error300 mb-2">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
              </p>
              <p className="text-sm">
                We do not warrant that the Service will be uninterrupted, timely, secure, or
                error-free, or that the results obtained from use of the Service will be accurate or
                reliable.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              6. Limitation of Liability
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              6.1 Exclusion of Damages
            </h3>
            <div className="p-4 bg-error500/10 border-2 border-error500 rounded-lg mb-3">
              <p className="font-semibold text-error700 dark:text-error300 mb-2">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL INVENTORYSUMO BE LIABLE FOR
                ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
              </p>
              <p className="text-sm">
                This includes but is not limited to: loss of profits, revenue, data, use, or
                goodwill; damages resulting from unauthorized access; interruption or cessation of
                transmission; bugs or errors; or any user data or content.
              </p>
            </div>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              6.2 Cap on Liability
            </h3>
            <p>
              Our total liability to you for all claims shall not exceed the greater of (A) the
              amount you paid us in the twelve (12) months preceding the event giving rise to
              liability, or (B) one hundred dollars ($100).
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              7. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless InventorySumo from and against all
              losses, expenses, damages, costs, claims, and demands resulting from: your use or
              misuse of the Service, your violation of these Terms, your violation of any rights of
              a third party, your User Data, or any business decisions you make based on information
              from the Service.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              8. Subscription, Payment, and Refund Terms
            </h2>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              8.1 Subscription Fees
            </h3>
            <p className="mb-3">
              The Service offers multiple subscription tiers (Basic, Premium, Enterprise) with
              different features and limitations. Subscription fees are billed in advance on a
              recurring basis. All payments are processed by Lemon Squeezy, LLC, which acts as the
              Merchant of Record for all transactions.
            </p>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              8.2 Refund Policy
            </h3>
            <div className="p-4 bg-warning500/10 border border-warning500 rounded-lg mb-3">
              <p className="font-semibold text-warning700 dark:text-warning300 mb-2">
                ALL SUBSCRIPTION FEES ARE NON-REFUNDABLE EXCEPT AS EXPRESSLY SET FORTH BELOW.
              </p>
            </div>
            <p className="mb-2">
              As a general policy, all fees paid for the Service are final and non-refundable. We do
              not provide refunds or credits for partial subscription periods, unused features, or
              dissatisfaction with the Service.
            </p>
            <p className="mb-2 font-semibold text-neutral900 dark:text-neutral100">
              Exceptions:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
              <li>
                <strong>Service Inaccessibility or Unusability:</strong> If the Service is
                materially inaccessible or unusable for an extended period due to issues within our
                control (not including scheduled maintenance, force majeure events, or issues caused
                by third-party services), you may request a refund or credit for the affected period.
                To be eligible, you must contact our support team within fourteen (14) days of the
                incident. Refunds under this exception are limited to a prorated amount corresponding
                to the period of inaccessibility and are issued at our sole discretion.
              </li>
              <li>
                <strong>As Required by Law:</strong> Refunds will be provided where required by
                applicable law or regulation.
              </li>
            </ul>
            <p className="mb-3 text-sm text-neutral500 dark:text-neutral400">
              <strong>Merchant of Record Refunds:</strong> All payments are processed through Lemon
              Squeezy, LLC, which serves as the Merchant of Record. Lemon Squeezy reserves the right
              to issue refunds at its discretion within sixty (60) days of purchase to prevent
              chargebacks. Such refunds are processed independently by Lemon Squeezy and are outside
              of InventorySumo's control.
            </p>

            <h3 className="text-base font-semibold text-neutral900 dark:text-neutral100 mt-4 mb-2">
              8.3 Cancellation
            </h3>
            <p>
              You may cancel your subscription at any time. Cancellation will take effect at the end
              of your current billing period. No refunds or credits will be issued for the remaining
              time in your current billing period upon cancellation.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              9. Data and Privacy
            </h2>
            <p className="mb-2">
              Your User Data is stored on third-party servers (Firebase/Google Cloud Platform). We
              implement reasonable security measures but cannot guarantee absolute security.
            </p>
            <div className="p-4 bg-warning500/10 border border-warning500 rounded-lg">
              <p className="font-semibold text-warning700 dark:text-warning300">
                YOU ARE SOLELY RESPONSIBLE FOR MAINTAINING BACKUP COPIES OF YOUR USER DATA.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              10. Service Modifications
            </h2>
            <p>
              We reserve the right to modify, suspend, or discontinue the Service (or any part
              thereof) at any time, with or without notice, for any reason. We do not guarantee that
              the Service will be available at all times.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-lg font-bold text-neutral900 dark:text-neutral100 mb-2">
              11. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of
              material changes by posting the new Terms on our website. Your continued use of the
              Service after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Acknowledgment */}
          <section className="border-t-2 border-neutral200 dark:border-neutral700 pt-6">
            <div className="p-4 bg-sumoPrimary/10 border-2 border-sumoPrimary rounded-lg">
              <p className="font-semibold text-neutral900 dark:text-neutral100">
                BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE
                BOUND BY THESE TERMS OF SERVICE.
              </p>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TermsOfServiceDialog
